/**
 * How a motion-graphics recipe's graphics are assigned techniques, kept here rather than on the
 * recipe because only motion-graphics recipes have techniques to assign.
 */
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { SkillName } from 'js/src/agent/skills/types';

/**
 * For a format whose every beat moves differently, one or two techniques per graphic is too few:
 * it needs a technique on each beat.
 */
export interface TechniquePolicy {
    /** One technique for each phase that performs something, placed on that phase. */
    perPhase: true;
    /** The most techniques one graphic is assigned. */
    maxPerGraphic: number;
    /** The techniques the chooser picks from. Absent offers the whole library. */
    vocabulary?: readonly SkillName[];
    /**
     * Whether each beat's kinetic type must be set at its size (a keyword filling the frame, a
     * phrase at the headline size), and a beat with nothing on screen blocks the save. Only a
     * format made of type alone asks this of its techniques.
     */
    checksKineticType?: true;
}

const KINETIC_TYPE_VOCABULARY: readonly SkillName[] = [
    SKILL_NAMES.letterMaskRise,
    SKILL_NAMES.glyphGrowStretch,
    SKILL_NAMES.glyphDropAndLand,
    SKILL_NAMES.glyphOvershootPop,
    SKILL_NAMES.wordStaggerEcho,
    SKILL_NAMES.figureGroundSplit,
    SKILL_NAMES.wordStrikeOut,
    SKILL_NAMES.wordTravelsDirection,
    SKILL_NAMES.orbitTextRing,
    SKILL_NAMES.motionBlurStreak,
    SKILL_NAMES.typeAsObject,
    SKILL_NAMES.strikethroughReplace,
    SKILL_NAMES.wordSwapInPlace,
    SKILL_NAMES.numberWheelRoll,
    SKILL_NAMES.headlineSlam,
    SKILL_NAMES.whipPanCut,
    SKILL_NAMES.zoomThroughTransition,
    SKILL_NAMES.wordPopSettle,
    SKILL_NAMES.velocityMatchedCut,
    SKILL_NAMES.underlineDraw,
    SKILL_NAMES.textureMaskedType,
    SKILL_NAMES.extrusionFlyIn,
    SKILL_NAMES.continuousPushIn,
    SKILL_NAMES.italicSkewSwing,
    SKILL_NAMES.driftingMeshGround,
];

const RECIPE_TECHNIQUE_POLICIES: Partial<Record<SkillName, TechniquePolicy>> = {
    [SKILL_NAMES.createKineticTypographyVideo]: {
        perPhase: true,
        maxPerGraphic: 2,
        vocabulary: KINETIC_TYPE_VOCABULARY,
        checksKineticType: true,
    },
};

/** The policy the recipe a video is built under sets, if it sets one. */
export function techniquePolicyFor(recipeName: string | undefined): TechniquePolicy | undefined {
    return recipeName ? RECIPE_TECHNIQUE_POLICIES[recipeName as SkillName] : undefined;
}

/** Without a policy, a graphic gets one technique or two. */
const DEFAULT_MAX_PICKS = 2;

export function maxPicksFor(policy?: TechniquePolicy): number {
    return policy?.maxPerGraphic ?? DEFAULT_MAX_PICKS;
}
